import axios from "axios";
import {
  DEFAULT_FETCH_TIMEOUT_MS,
  DEFAULT_GTFS_STATION_RESULT_LIMIT,
  DEFAULT_GTFS_STOP_RESULT_LIMIT,
  DEFAULT_GTFS_TOTAL_RESULT_LIMIT,
} from "../../Constants/Constants.js";
import { DataValidationError } from "../../Errors/DataValidationError.js";
import { GtfsApiError } from "../../Errors/GtfsApiError.js";
import { LocationType } from "../../Models/GeocodeResponse.js";
import { GtfsTransformationService } from "./GtfsTransformationService.js";

const DIGITS_ONLY_REGEX = /^\d{2,5}$/;

export class GtfsGeocodingService {
  /** @type {number} */
  _defaultLimitForStation;
  /** @type {number} */
  _defaultLimitForOthers;
  /** @type {number} */
  _defaultLimitTotal;
  /** @type {AxiosInstance} */
  _axiosInstance;
  /** @type {Logger} */
  _logger;

  _gtfsTransformationService;

  /**
   * Construct a GTFS geocoding service.
   * @param {GtfsApiOptions} gtfs GTFS API configuration options
   * @param {Logger} logger Logger
   * @param {GtfsLimitOptions} defaultLimit Default maximum number of results the GTFS API should return
   */
  constructor(gtfs, logger, defaultLimit) {
    const { apiBaseUrl, apiKey, timeout } = gtfs;
    this._defaultLimitForStation = defaultLimit?.station ?? DEFAULT_GTFS_STATION_RESULT_LIMIT;
    this._defaultLimitForOthers = defaultLimit?.others ?? DEFAULT_GTFS_STOP_RESULT_LIMIT;
    this._defaultLimitTotal = defaultLimit?.total ?? DEFAULT_GTFS_TOTAL_RESULT_LIMIT;
    this._axiosInstance = axios.create({
      baseURL: apiBaseUrl,
      timeout: timeout ?? DEFAULT_FETCH_TIMEOUT_MS,
      headers: {
        "ocp-apim-subscription-key": apiKey,
      },
    });
    this._logger = logger;
    this._gtfsTransformationService = new GtfsTransformationService();
  }

  /**
   * Send a forwardGeocode query to the GTFS API.
   * @param {ForwardGeocodeOptions} options Forward geocoding options
   * @returns {Promise<PromiseSettledResult<GeocodeResultFeatures>>} Geocoding result features
   */
  async forwardGeocode({ query, proximity, limit }) {
    try {
      const stationLimit = limit?.gtfs?.station ?? this._defaultLimitForStation;
      const othersLimit = limit?.gtfs?.others ?? this._defaultLimitForOthers;
      const totalLimit = limit?.gtfs?.total ?? this._defaultLimitTotal;
      if ((stationLimit <= 0 && othersLimit <= 0) || totalLimit <= 0) {
        return { status: "fulfilled", value: [] };
      }

      const params = {
        "filter[stop_code]": `${query}*`,
        "filter[stop_name]": `*${query}*`,
      };
      if (proximity) {
        params.proximity = `${proximity[1]},${proximity[0]}`;
      }

      let gtfsResponse;
      try {
        gtfsResponse = await this._axiosInstance.get("/gtfs/v3/stops", { params });
      } catch (error) {
        throw new GtfsApiError(error.message, error.response?.data?.errors);
      }

      this._verifyGtfsResponse(gtfsResponse.data);

      const results = this._isStopCodeOnlySearch(query)
        ? gtfsResponse.data.data
        : this._excludeStopWithParents(gtfsResponse.data.data);

      const value = this._gtfsTransformationService.transformGtfsData(results, stationLimit, othersLimit, totalLimit);
      return { status: "fulfilled", value };
    } catch (error) {
      this._logger?.error?.(`GTFS Forward geocoder: ${error.name} - ${error.message}`);
      return {
        status: "rejected",
        reason: error,
      };
    }
  }

  _verifyGtfsResponse(response) {
    const gtfsData = response?.data;
    if (!gtfsData) throw new DataValidationError("GTFS data incomplete");

    gtfsData.forEach(this._verifyGtfsResponseAttribute);
  }

  _verifyGtfsResponseAttribute(gtfsResponseData) {
    const gtfsItemAttrs = gtfsResponseData?.attributes;
    const gtfsStopId = gtfsItemAttrs?.stop_id;

    if (!gtfsItemAttrs) throw new DataValidationError("GTFS stop without attributes");
    if (!gtfsStopId) throw new DataValidationError("GTFS stop without stop_id");
    if (typeof gtfsStopId !== "string") throw new DataValidationError(`GTFS stop with invalid stop_id ${gtfsStopId}`);
    if (!gtfsItemAttrs.stop_code) throw new DataValidationError(`GTFS stop ${gtfsStopId} without stop_code`);
    if (typeof gtfsItemAttrs.stop_code !== "string")
      throw new DataValidationError(`GTFS stop ${gtfsStopId} with invalid stop_code ${gtfsItemAttrs.stop_code}`);
    if (!gtfsItemAttrs.stop_lat) throw new DataValidationError(`GTFS stop ${gtfsStopId} without stop_lat`);
    if (typeof gtfsItemAttrs.stop_lat !== "number")
      throw new DataValidationError(`GTFS stop ${gtfsStopId} with invalid stop_lat ${gtfsItemAttrs.stop_lat}`);
    if (!gtfsItemAttrs.stop_lon) throw new DataValidationError(`GTFS stop ${gtfsStopId} without stop_lon`);
    if (typeof gtfsItemAttrs.stop_lon !== "number")
      throw new DataValidationError(`GTFS stop ${gtfsStopId} with invalid stop_lon ${gtfsItemAttrs.stop_lon}`);
    if (!gtfsItemAttrs.stop_name) throw new DataValidationError(`GTFS stop ${gtfsStopId} without stop_name`);
    if (typeof gtfsItemAttrs.stop_name !== "string")
      throw new DataValidationError(`GTFS stop ${gtfsStopId} with invalid stop_name ${gtfsItemAttrs.stop_name}`);
    // below is equivalent to the more explicit but less concise (location_type === null || location_type === undefined)
    if (gtfsItemAttrs.location_type == null)
      throw new DataValidationError(`GTFS stop ${gtfsStopId} without location_type`);
    if (typeof gtfsItemAttrs.location_type !== "number")
      throw new DataValidationError(
        `GTFS stop ${gtfsStopId} with invalid location_type ${gtfsItemAttrs.location_type}`
      );
  }

  /**
   * Checks if the query matches a "stop code only" search.
   * @param {string} query The query string
   * @returns {boolean} Returns true, if the query matches a "stop code only" search, false otherwise.
   */
  _isStopCodeOnlySearch(query) {
    return !!query.match(DIGITS_ONLY_REGEX);
  }

  /**
   * Removes all child stops with parents from an array of GTFS data.
   * @param {GtfsStop[]} gtfsData GTFS data
   * @returns {GtfsStop[]} Processed GTFS data
   */
  _excludeStopWithParents(results) {
    return results.filter(
      ({ attributes }) => attributes?.location_type === LocationType.STATION || !attributes?.parent_station
    );
  }
}
